import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components";
import { Dialog } from "@/components/ui/dialog";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import {
  CampaignList,
  CampaignDetail,
  CampaignForm,
} from "@/features/campaign/components";
import { campaignService } from "@/features/campaign/services/campaign.service";
import type {
  Campaign,
  CreateCampaignParams,
  UpdateCampaignParams,
} from "@/features/campaign/interfaces";

type ViewMode = "list" | "create" | "edit" | "detail";

const CAMPAIGNS_QUERY_KEY = ["campaigns"];

export const Campaigns: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [campaignToDelete, setCampaignToDelete] = useState<{ id: number; title: string } | null>(null);
  const queryClient = useQueryClient();

  // Fetch all campaigns
  const {
    data: campaigns = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: CAMPAIGNS_QUERY_KEY,
    queryFn: () => campaignService.getAllCampaigns(),
  });

  // Fetch single campaign for detail view
  const { data: campaignDetail } = useQuery({
    queryKey: ["campaigns", selectedCampaignId],
    queryFn: () => campaignService.getCampaignById(selectedCampaignId!),
    enabled: !!selectedCampaignId && viewMode === "detail",
  });

  const displayCampaign = campaignDetail ?? selectedCampaign;

  // Create campaign mutation
  const createMutation = useMutation({
    mutationFn: (params: CreateCampaignParams) =>
      campaignService.createCampaign(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
      toast.success("Campaign created successfully");
      setViewMode("list");
    },
    onError: (error: unknown) => {
      toast.error(
        typeof error === "string" ? error : "Failed to create campaign"
      );
    },
  });

  // Update campaign mutation
  const updateMutation = useMutation({
    mutationFn: (params: UpdateCampaignParams) =>
      campaignService.updateCampaign(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["campaigns", selectedCampaignId] });
      toast.success("Campaign updated successfully");
      setViewMode("list");
      setSelectedCampaign(null);
      setSelectedCampaignId(null);
    },
    onError: (error: unknown) => {
      toast.error(
        typeof error === "string" ? error : "Failed to update campaign"
      );
    },
  });

  // Delete campaign mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => campaignService.deleteCampaign({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["campaigns", selectedCampaignId] });
      toast.success("Campaign deleted successfully");
      setViewMode("list");
      setSelectedCampaign(null);
      setSelectedCampaignId(null);
      setCampaignToDelete(null);
    },
    onError: (error: unknown) => {
      toast.error(
        typeof error === "string" ? error : "Failed to delete campaign"
      );
    },
  });

  const handleCreateNew = () => {
    setSelectedCampaign(null);
    setSelectedCampaignId(null);
    setViewMode("create");
  };

  const handleEdit = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setSelectedCampaignId(campaign.id);
    setViewMode("edit");
  };

  const handleDeleteClick = (campaignId: number) => {
    const campaign = campaigns.find((c) => c.id === campaignId) ?? displayCampaign ?? null;
    setCampaignToDelete(
      campaign ? { id: campaign.id, title: campaign.title } : { id: campaignId, title: "This campaign" }
    );
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;
    await deleteMutation.mutateAsync(campaignToDelete.id);
  };

  const handleView = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setSelectedCampaignId(campaign.id);
    setViewMode("detail");
  };

  const handleBack = () => {
    setViewMode("list");
    setSelectedCampaign(null);
    setSelectedCampaignId(null);
  };

  const handleFormSubmit = async (
    data: CreateCampaignParams | UpdateCampaignParams
  ) => {
    if (selectedCampaign) {
      await updateMutation.mutateAsync(data as UpdateCampaignParams);
    } else {
      await createMutation.mutateAsync(data as CreateCampaignParams);
    }
  };

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
          <p className="text-destructive">
            Failed to load campaigns. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {viewMode === "list" && (
        <CampaignList
          campaigns={campaigns}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onCreateNew={handleCreateNew}
          onView={handleView}
          isLoading={isLoading}
        />
      )}

      {viewMode === "detail" && displayCampaign && (
        <CampaignDetail
          campaign={displayCampaign}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onBack={handleBack}
        />
      )}

      <Dialog
        open={campaignToDelete !== null}
        onOpenChange={(open) => !open && setCampaignToDelete(null)}
        title="Delete campaign?"
        description={
          campaignToDelete
            ? `This will permanently delete "${campaignToDelete.title}". This action cannot be undone.`
            : "This action cannot be undone."
        }
        size="sm"
      >
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setCampaignToDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={handleDeleteConfirm}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </Dialog>

      {(viewMode === "create" || viewMode === "edit") && (
        <div className="max-w-4xl mx-auto w-full">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h1 className="text-2xl sm:text-3xl font-bold">
                {viewMode === "edit" ? "Edit Campaign" : "Create Campaign"}
              </h1>
              <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0 self-end sm:self-auto">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-muted-foreground">
              {viewMode === "edit"
                ? "Update your campaign information"
                : "Create a new campaign with goals, CTAs, and content"}
            </p>
          </div>

          <div className="bg-card border border-input rounded-lg p-4 sm:p-6">
            <CampaignForm
              campaign={viewMode === "edit" ? selectedCampaign ?? undefined : undefined}
              onSubmit={handleFormSubmit}
              onCancel={handleBack}
              isLoading={createMutation.isPending || updateMutation.isPending}
            />
          </div>
        </div>
      )}
    </div>
  );
};
